import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import StandardTable from "../../common/StandardTable";
import PageToolbar from "../../common/PageToolbar";
import DateRangeFilter from "../../common/DateRangeFilter";
import StatusChip from "../../common/StatusChip";
import useDebounce from "../../../hooks/useDebounce";

import { getInvoices } from "../../../redux/actions/invoiceActions";
import { createFiscalInvoice } from "../../../redux/actions/fiscalActions";

const FiscalizeInvoiceModal = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
    .toISOString()
    .split("T")[0];
  const today = new Date().toISOString().split("T")[0];

  const { invoices, pagination, loadingInvoices, errorLoadingInvoices } =
    useSelector((state) => state.invoice);
  const { loadingFiscalInvoiceCreate } = useSelector((state) => state.fiscal);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 5 });

  const fetchLocalInvoices = useCallback(() => {
    dispatch(
      getInvoices({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (open) fetchLocalInvoices();
  }, [open, fetchLocalInvoices]);
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  const handleFiscalize = (invoiceId) => {
    dispatch(createFiscalInvoice({ invoiceId }));
  };

  const columns = useMemo(
    () => [
      {
        id: "invoiceNumber",
        label: "Invoice #",
        minWidth: 120,
        format: (val) => <b style={{ color: "#c77dff" }}>{val}</b>,
      },
      { id: "customerBusinessName", label: "Customer", minWidth: 150 },
      {
        id: "issueDate",
        label: "Issue Date",
        minWidth: 100,
        format: (val) => new Date(val).toLocaleDateString(),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 100,
        format: (val) => <StatusChip status={val} />,
      },
      {
        id: "total",
        label: "Amount",
        minWidth: 100,
        align: "right",
        format: (val) => Number(val).toLocaleString(),
      },
    ],
    [],
  );

  return (
    <Dialog
      open={open}
      onClose={loadingFiscalInvoiceCreate ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        Push Local Invoice to eTIMS
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, minHeight: 400 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select a local invoice to generate a compliant KRA eTIMS Tax Invoice.
        </Typography>

        <PageToolbar
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          searchPlaceholder="Search local invoice #..."
        >
          <DateRangeFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </PageToolbar>

        <Box sx={{ mt: 2 }}>
          <StandardTable
            columns={columns}
            data={invoices}
            loading={loadingInvoices}
            error={errorLoadingInvoices}
            onRetry={fetchLocalInvoices}
            page={pageConfig.page}
            count={pagination?.totalElements || 0}
            rowsPerPage={pageConfig.limit}
            onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
            onRowsPerPageChange={(l) =>
              setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
            }
            enableColumnManagement={false}
            rowAction={(row) => (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<CloudUploadIcon />}
                disabled={loadingFiscalInvoiceCreate}
                onClick={() => handleFiscalize(row.id)}
              >
                {loadingFiscalInvoiceCreate ? "Pushing..." : "Fiscalize"}
              </Button>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loadingFiscalInvoiceCreate}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default FiscalizeInvoiceModal;
